import { useMutation, useQueryClient } from "@tanstack/react-query"
import { AlertCircle } from "lucide-react"
import { useId, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { CampoRif } from "./CampoRif"

/**
 * Alta y edición de un cliente.
 *
 * ⚠ **El código no se puede cambiar al editar.** Es la identidad con la que el
 * cliente aparece en expedientes, cotizaciones y cuentas por cobrar ya emitidas;
 * cambiarlo dejaría esos documentos apuntando a un código que ya no existe. El
 * servidor tampoco lo admite en el `PATCH`, y aquí se pinta de sólo lectura para que
 * se vea por qué en vez de descubrirlo con un campo que no hace nada.
 *
 * ⚠ **Un cliente no se borra: se desactiva.** Tiene expedientes y cobros colgando, y
 * borrarlo destruiría el rastro de lo que se le facturó. Por eso el único control
 * destructivo es la casilla de activo, y sólo al editar.
 */

export type Cliente = {
  id: number
  codigo: string
  razon_social: string
  rif: string
  email: string | null
  telefono: string | null
  activo: boolean
}

type Props = {
  /** `null` para dar de alta; el cliente para editarlo. */
  cliente: Cliente | null
  onCerrar: () => void
}

export function FormularioCliente({ cliente, onCerrar }: Props) {
  const clienteQuery = useQueryClient()
  const idBase = useId()
  const editando = cliente !== null

  const [codigo, setCodigo] = useState(cliente?.codigo ?? "")
  const [razonSocial, setRazonSocial] = useState(cliente?.razon_social ?? "")
  const [rif, setRif] = useState(cliente?.rif ?? "J-")
  const [email, setEmail] = useState(cliente?.email ?? "")
  const [telefono, setTelefono] = useState(cliente?.telefono ?? "")
  const [activo, setActivo] = useState(cliente?.activo ?? true)

  const guardar = useMutation({
    mutationFn: () => {
      const comun = {
        razon_social: razonSocial.trim(),
        rif: rif.trim(),
        email: email.trim() === "" ? null : email.trim(),
        telefono: telefono.trim() === "" ? null : telefono.trim(),
      }
      return editando
        ? api.patch(`/api/v1/clientes/${cliente.id}`, { ...comun, activo })
        : api.post("/api/v1/clientes", { ...comun, codigo: codigo.trim() })
    },
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["clientes"] })
      onCerrar()
    },
  })

  const listo =
    razonSocial.trim().length >= 3 &&
    rif.trim().length >= 3 &&
    (editando || codigo.trim().length >= 2)
  const borde = { borderColor: "var(--border)" }

  return (
    <form
      aria-label={editando ? "Editar cliente" : "Nuevo cliente"}
      onSubmit={(e) => {
        e.preventDefault()
        if (listo) guardar.mutate()
      }}
      className="superficie p-4"
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-sm" htmlFor={`${idBase}-codigo`}>
          Código
          <input
            id={`${idBase}-codigo`}
            className="campo mt-1 disabled:opacity-60"
            value={codigo}
            disabled={editando}
            onChange={(e) => setCodigo(e.target.value)}
            placeholder="IMPCAR"
            aria-describedby={editando ? `${idBase}-codigo-pista` : undefined}
          />
        </label>
        {editando && (
          <p
            id={`${idBase}-codigo-pista`}
            className="-mt-2 text-xs"
            style={{ color: "var(--fg-muted)" }}
          >
            No se cambia: hay expedientes y cobros emitidos con él.
          </p>
        )}

        <label className="block text-sm" htmlFor={`${idBase}-razon`}>
          Razón social
          <input
            id={`${idBase}-razon`}
            className="campo mt-1"
            value={razonSocial}
            onChange={(e) => setRazonSocial(e.target.value)}
            placeholder="Importadora Caribe, C.A."
          />
        </label>

        <CampoRif id={`${idBase}-rif`} valor={rif} onCambiar={setRif} />

        <label className="block text-sm" htmlFor={`${idBase}-email`}>
          Correo
          <input
            id={`${idBase}-email`}
            type="email"
            className="campo mt-1"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>

        <label className="block text-sm" htmlFor={`${idBase}-telefono`}>
          Teléfono
          <input
            id={`${idBase}-telefono`}
            className="campo mt-1 tabular-nums"
            value={telefono}
            onChange={(e) => setTelefono(e.target.value)}
            placeholder="0412-1234567"
            aria-describedby={`${idBase}-telefono-pista`}
          />
        </label>
        {/* El servidor lo normaliza a E.164 sin el «+», que es lo que consume el
            enlace de WhatsApp. Se teclea como se dicta. */}
        <p
          id={`${idBase}-telefono-pista`}
          className="-mt-2 text-xs"
          style={{ color: "var(--fg-muted)" }}
        >
          Se guarda en formato internacional para el enlace de WhatsApp.
        </p>

        {editando && (
          <label className="flex items-center gap-2 self-end text-sm">
            <input type="checkbox" checked={activo} onChange={(e) => setActivo(e.target.checked)} />
            Activo
          </label>
        )}
      </div>

      {guardar.isError && (
        <p
          role="alert"
          className="mt-3 flex items-center gap-2 text-sm"
          style={{ color: "var(--destructive)" }}
        >
          <AlertCircle size={16} aria-hidden={true} />
          {guardar.error instanceof ErrorApi
            ? guardar.error.message
            : "No se pudo guardar el cliente."}
        </p>
      )}

      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCerrar}
          className="rounded-[var(--radius-control)] border px-3 py-1.5 text-sm"
          style={borde}
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={!listo || guardar.isPending}
          className="rounded-[var(--radius-control)] px-3 py-1.5 text-sm disabled:opacity-50"
          style={{ background: "var(--primary)", color: "var(--primary-fg)" }}
        >
          {guardar.isPending ? "Guardando…" : editando ? "Guardar cambios" : "Crear cliente"}
        </button>
      </div>
    </form>
  )
}
